// Wahlkampfbudget: Parteispenden finanzieren Werbung in verschiedenen Kanälen. Jeder Kanal erreicht
// andere Wählergruppen; wie viel das bringt, hängt davon ab, wie offen die Gruppe für die Partei ist.
import { GROUPS, WEIGHT } from '../content/axes';
import { ISS } from '../content/states';
import { clamp } from './util';

export type Channel = 'social' | 'tv' | 'plakat' | 'haus';
export type Alloc = Partial<Record<Channel, number>>;

export const CHANNELS: Record<Channel, { n: string; d: string; reach: Record<string, number>; k: number; sat: number }> = {
  social: { n: 'Social Media', d: 'Gezielte Anzeigen und Clips. Erreicht vor allem Junge, kaum Rentner.', reach: { jung: 1, fam: .55, arb: .45, unt: .3, rent: .12 }, k: 1.5, sat: 2.5 },
  tv: { n: 'TV-Spots', d: 'Teuer, aber mit großer Reichweite, besonders bei Älteren und Familien.', reach: { rent: 1, fam: .6, arb: .6, unt: .35, jung: .2 }, k: 1.7, sat: 5 },
  plakat: { n: 'Plakate', d: 'Machen die Partei bekannt. Wirkt breit, aber nur oberflächlich; für neue Parteien wichtiger.', reach: { rent: .5, fam: .5, arb: .5, unt: .4, jung: .4 }, k: .9, sat: 2 },
  haus: { n: 'Haustür & Infostände', d: 'Persönliche Gespräche. Günstig, überzeugt Arbeitnehmer und Ältere, erreicht aber wenige.', reach: { arb: .7, rent: .65, fam: .5, jung: .3, unt: .2 }, k: 1, sat: 1.2 },
};
export const CHANNEL_IDS = Object.keys(CHANNELS) as Channel[];
/** Schrittweite beim Verteilen (Mio €). */
export const AD_STEP = .5;

export interface Funding { total: number; parts: { n: string; v: number; d: string }[] }

/** Spendeneinnahmen vor dem Wahlkampf (Mio €). share: letzte Umfrage bzw. Startwert; pos: Programm; first: erste Wahl (neue Partei). */
export function funding(o: { share: number; pos: Record<string, number>; first: boolean; arbg?: number; soz?: number; gew?: number; lastShare?: number | null }): Funding {
  const r1 = (x: number) => Math.round(x * 2) / 2;
  const biz = clamp(1 + (o.pos.buero ?? 0) * .6 - (o.pos.steuern ?? 0) * .5 + ((o.arbg ?? 50) - 50) / 25, .5, 4);
  const civil = clamp(.5 + (o.pos.sozial ?? 0) * .3 + ((o.gew ?? 50) + (o.soz ?? 50) - 100) / 50, .5, 2.5);
  const parts = [
    { n: 'Mitgliedsbeiträge', v: r1(1.5 + o.share * .12), d: 'je größer die Partei, desto mehr Mitglieder' },
    { n: 'Kleinspenden', v: r1(1 + o.share * .08), d: 'Unterstützer im ganzen Land' },
    { n: 'Großspenden aus der Wirtschaft', v: r1(biz), d: 'mehr bei wirtschaftsfreundlichem Programm' },
    { n: 'Gewerkschaften und Vereine', v: r1(civil), d: 'mehr bei sozialem Programm' },
  ];
  if (!o.first && o.lastShare) parts.push({ n: 'Staatliche Teilfinanzierung', v: r1(o.lastShare * .15), d: 'nach dem letzten Wahlergebnis' });
  else parts.push({ n: 'Startkredit für die neue Partei', v: 2, d: 'einmalig zur ersten Wahl' });
  return { total: parts.reduce((s, p) => s + p.v, 0), parts };
}

/** Wie offen ist eine Wählergruppe für die Partei (0,3 … 1,6)? Kernthemen, die die Gruppe betreffen, und ihre Stimmung zählen. */
export function receptivity(themes: string[], mood: Record<string, number>, gov: boolean): Record<string, number> {
  const r: Record<string, number> = {};
  for (const g of Object.keys(GROUPS)) {
    const imp = themes.reduce((s, id) => s + (ISS[id]?.imp?.[g] || 0), 0);
    const m = mood[g] ?? 45;
    r[g] = clamp(.65 + imp * .25 + (gov ? (m - 45) / 40 : (45 - m) / 60), .3, 1.6);
  }
  return r;
}

/** Wirkung der Werbung in Prozentpunkten, gesamt und je Wählergruppe. first: Plakate wirken für neue Parteien stärker. */
export function adEffect(alloc: Alloc, recept: Record<string, number>, first = false): { tot: number; byGroup: Record<string, number>; byChannel: Record<string, number> } {
  const byGroup: Record<string, number> = {}, byChannel: Record<string, number> = {};
  for (const g of Object.keys(GROUPS)) byGroup[g] = 0;
  for (const c of CHANNEL_IDS) {
    const C = CHANNELS[c], x = Math.max(0, alloc[c] || 0);
    const impact = C.k * (c === 'plakat' && first ? 1.4 : 1) * (1 - Math.exp(-x / C.sat));
    let sum = 0;
    for (const g of Object.keys(GROUPS)) { const v = impact * (C.reach[g] || 0) * WEIGHT[g] * (recept[g] ?? 1); byGroup[g] += v; sum += v; }
    byChannel[c] = sum;
  }
  const tot = Object.values(byGroup).reduce((s, v) => s + v, 0);
  return { tot, byGroup, byChannel };
}

/** Verteilung prüfen: nur bekannte Kanäle, Schritte von 0,5, Summe höchstens Budget. */
export function setAlloc(alloc: Alloc, ch: string, v: number, budget: number): Alloc {
  if (!(CHANNEL_IDS as string[]).includes(ch)) return alloc;
  const next = { ...alloc, [ch]: Math.max(0, Math.round((Number(v) || 0) / AD_STEP) * AD_STEP) } as Alloc;
  const sum = CHANNEL_IDS.reduce((s, c) => s + (next[c] || 0), 0);
  if (sum > budget + 1e-9) next[ch as Channel] = Math.max(0, (next[ch as Channel] || 0) - (sum - budget));
  next[ch as Channel] = Math.floor((next[ch as Channel] || 0) / AD_STEP + 1e-9) * AD_STEP;
  return next;
}
export const spent = (a: Alloc) => CHANNEL_IDS.reduce((s, c) => s + (a[c] || 0), 0);
